import { BannerStatus, KeyHealthScope } from "types/keyHealth"
import EnableEncryptionIcon from "assets/images/icons/enable-encryption-icon.svg"
import WarningEncryptIcon from "assets/images/icons/warning-encrypt.svg"
import LockIcon from "assets/images/icons/icon-lock.svg"

interface KeyHealthStatusBannerProps {
  status: BannerStatus
  scope: KeyHealthScope
  isAutoFixing: boolean
  onClickYellow: () => void
}

const SCOPE_COPY: Record<
  KeyHealthScope,
  {
    greenTitle: string
    greenSubtitle: string
    yellowTitle: string
    yellowFixingSubtitle: string
    yellowFailedSubtitle: string
  }
> = {
  global: {
    greenTitle: "Conversation encrypted",
    greenSubtitle: "Encryption key is healthy",
    yellowTitle: "This conversation can't be fully opened",
    yellowFixingSubtitle: "Restoring conversation access automatically...",
    yellowFailedSubtitle: "Could not restore conversation access automatically",
  },
  projects: {
    greenTitle: "Projects encrypted",
    greenSubtitle: "Encryption keys are healthy",
    yellowTitle: "Some projects can't be opened",
    yellowFixingSubtitle: "Restoring project access automatically...",
    yellowFailedSubtitle: "Could not restore all projects automatically",
  },
  projectDetail: {
    greenTitle: "Project encrypted",
    greenSubtitle: "Encryption key is healthy",
    yellowTitle: "This project can't be fully opened",
    yellowFixingSubtitle: "Restoring project access automatically...",
    yellowFailedSubtitle: "Could not restore this project automatically",
  },
  projectComponent: {
    greenTitle: "Component encrypted",
    greenSubtitle: "Encryption key is healthy",
    yellowTitle: "This component can't be fully opened",
    yellowFixingSubtitle: "Restoring component access automatically...",
    yellowFailedSubtitle: "Could not restore this component automatically",
  },
  buildComponent: {
    greenTitle: "Build component encrypted",
    greenSubtitle: "Encryption key is healthy",
    yellowTitle: "This build component can't be fully opened",
    yellowFixingSubtitle: "Restoring build component access automatically...",
    yellowFailedSubtitle: "Could not restore this build component automatically",
  },
  build: {
    greenTitle: "Builds encrypted",
    greenSubtitle: "Encryption keys are healthy",
    yellowTitle: "Some encrypted items can't be opened",
    yellowFixingSubtitle: "Restoring build data access automatically...",
    yellowFailedSubtitle: "Could not restore all build data automatically",
  },
}

const BANNER_CONFIG = {
  green: {
    borderColor: "#16a34a",
    background: "#f0fdf4",
    iconBg: "rgba(22, 163, 74, 0.1)",
    iconFilter:
      "brightness(0) saturate(100%) invert(39%) sepia(95%) saturate(500%) hue-rotate(100deg) brightness(0.9)",
    icon: EnableEncryptionIcon,
    titleColor: "#15803d",
    title: "Encrypted",
    subtitleColor: "#555555",
    subtitle: "All keys loaded successfully",
    cursor: "default",
  },
  yellow: {
    borderColor: "#d97706",
    background: "#fffbeb",
    iconBg: "rgba(217, 119, 6, 0.1)",
    iconFilter:
      "brightness(0) saturate(100%) invert(55%) sepia(60%) saturate(800%) hue-rotate(15deg) brightness(0.95)",
    icon: WarningEncryptIcon,
    titleColor: "#b45309",
    title: "Some encrypted items can't be opened",
    subtitleColor: "#555555",
    subtitle: "",
    cursor: "pointer",
  },
  gray: {
    borderColor: "#9ca3af",
    background: "#f9fafb",
    iconBg: "rgba(156, 163, 175, 0.15)",
    iconFilter:
      "brightness(0) saturate(100%) invert(65%) sepia(5%) saturate(300%) hue-rotate(180deg)",
    icon: LockIcon,
    titleColor: "#6b7280",
    title: "Not encrypted",
    subtitleColor: "#9ca3af",
    subtitle: "This content is not encrypted",
    cursor: "default",
  },
  checking: {
    borderColor: "#3b82f6",
    background: "#eff6ff",
    iconBg: "rgba(59, 130, 246, 0.12)",
    iconFilter:
      "brightness(0) saturate(100%) invert(42%) sepia(77%) saturate(1835%) hue-rotate(204deg) brightness(100%) contrast(95%)",
    icon: WarningEncryptIcon,
    titleColor: "#1d4ed8",
    title: "Checking encryption status",
    subtitleColor: "#1e40af",
    subtitle: "Please wait a moment",
    cursor: "default",
  },
}

export const KeyHealthStatusBanner = ({
  status,
  scope,
  isAutoFixing,
  onClickYellow,
}: KeyHealthStatusBannerProps) => {
  if (status === "hidden") return null

  const config = BANNER_CONFIG[status]
  const scopeCopy = SCOPE_COPY[scope]

  const yellowSubtitle = isAutoFixing
    ? scopeCopy.yellowFixingSubtitle
    : scopeCopy.yellowFailedSubtitle
  const subtitle =
    status === "yellow"
      ? yellowSubtitle
      : status === "green"
        ? scopeCopy.greenSubtitle
        : config.subtitle
  const title =
    status === "yellow"
      ? scopeCopy.yellowTitle
      : status === "green"
        ? scopeCopy.greenTitle
        : config.title

  return (
    <>
      {(status === "yellow" || status === "checking") && (
        <style>{`@keyframes khBannerSpin { to { transform: rotate(360deg); } }`}</style>
      )}
      <div
        style={{
          flexShrink: 0,
          position: "relative",
          zIndex: 10001,
          background: config.background,
          borderBottom: "1px solid #E4E4E4",
          borderLeft: `3px solid ${config.borderColor}`,
          padding: "6px 24px 6px 15px",
          cursor: config.cursor,
        }}
        className="transition-all duration-300"
      >
        <div className="flex items-center gap-3 h-full">
          <div
            style={{
              background: config.iconBg,
              borderRadius: "8px",
              padding: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={config.icon}
              alt="key health"
              style={{
                width: 18,
                height: 18,
                filter: config.iconFilter,
              }}
            />
          </div>

          <div className="flex items-center gap-1 flex-1">
            <p
              style={{
                color: config.titleColor,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: "20px",
              }}
            >
              {title}
            </p>
            {subtitle && (
              <p
                style={{
                  color: config.subtitleColor,
                  fontSize: 13,
                  lineHeight: "20px",
                }}
              >
                — {subtitle}
              </p>
            )}
          </div>

          {/* Right side: spinner when fixing, "View details" when done with errors */}
          {(status === "yellow" || status === "checking") && (
            <div style={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
              {status === "checking" || isAutoFixing ? (
                <div
                  style={{
                    width: 16,
                    height: 16,
                    border: status === "checking" ? "2px solid #bfdbfe" : "2px solid #fde68a",
                    borderTopColor: status === "checking" ? "#1d4ed8" : "#b45309",
                    borderRadius: "50%",
                  }}
                />
              ) : (
                <p
                  style={{
                    color: "#b45309",
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: "20px",
                    textDecoration: "underline",
                    cursor: 'pointer'
                  }}
                  onClick={status === "yellow" ? onClickYellow : undefined}
                >
                  View details
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
